/**
 * FleetManager V3 - Dashboard Component
 * Adaptive dashboard based on user profile
 * Split into two tabs: Actions (operational) and Suivi (monitoring)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { useProfile } from '../../hooks/useProfile.js';
import { useDashboardData } from '../../hooks/useDashboardData.js';
import { useNavigation } from '../../context/NavigationContext.jsx';
import { DashboardTabs, TABS } from './DashboardTabs.jsx';
import {
  AlertsWidget,
  FleetStatusWidget,
  QuickActionsWidget,
  DeadlinesWidget,
  CostsWidget,
  ClaimsWidget,
  ActivityWidget,
  SitesWidget,
  DriverAvailabilityWidget,
  TeamsWidget,
  CalendarWidget,
  ResourceAlertsWidget,
  PoolStatusWidget
} from './widgets/index.js';

// Onboarding completeness widget
import { CompletenessCard } from '../onboarding/index.js';

export function Dashboard({ onAction }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('actions');
  const { profile, isWidgetEnabled } = useProfile();
  const { data, loading, error, refresh } = useDashboardData();

  // Handle navigation to a view — route through NavigationContext so viewParams
  // are available to destination views via useNavigation()
  const { navigateTo } = useNavigation();
  const handleNavigate = (viewId, params = {}) => {
    navigateTo(viewId, params);
  };

  // Handle quick actions
  const handleAction = (actionId) => {
    console.log('Action:', actionId);
    onAction?.(actionId);
  };

  // Determine grid layout based on profile (2-col for dense/minimal, 3-col for ops/director)
  const gridCols = ['pme_simple', 'conducteur'].includes(profile.id) ? 'grid-cols-2' : 'grid-cols-3';

  // Le conducteur n'a rien à « suivre » : l'onglet Suivi ne lui montrait que
  // des widgets déjà retirés un à un (calendrier du parc, complétude). Il ne
  // reste donc qu'un onglet — et un seul onglet ne se dessine pas.
  // (Demande JGS 2026-09-14.)
  const isDriver = profile.id === 'conducteur';
  const tabs = isDriver ? TABS.filter((tab) => tab.id === 'actions') : TABS;
  const currentTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'actions';

  // Loading state
  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="text-gray-500">{t('dashboard.loading')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={24} />
          <div className="flex-1">
            <div className="text-gray-900 font-medium">{t('common.states.loadError')}</div>
            <div className="text-sm text-gray-600">{error}</div>
          </div>
          <button
            onClick={refresh}
            className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-sm transition-colors"
          >
            {t('common.actions.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Tab 1: Actions - Operational dashboard
  const renderActionsTab = () => (
    <div className="space-y-4">
      {/* Main widgets grid */}
      <div className={`grid gap-4 ${gridCols}`}>
        {/* Alerts - spans 2 cols for fleet_manager */}
        {isWidgetEnabled('alerts_priority') && (
          <div className={profile.id === 'directeur_flotte' ? 'col-span-2' : ''} data-tour="dashboard-alerts">
            <AlertsWidget alerts={data.alerts} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Quick Actions */}
        {isWidgetEnabled('quick_actions') && (
          <div data-tour="quick-actions">
            <QuickActionsWidget onAction={handleAction} />
          </div>
        )}

        {/* Pool status - le tableau de bord du conducteur : SA garde, SES réservations */}
        {isWidgetEnabled('pool_status') && (
          <div data-tour="dashboard-pool">
            <PoolStatusWidget pool={data.pool} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Fleet Status - clickable */}
        {isWidgetEnabled('vehicles_status') && (
          <div data-tour="dashboard-stats">
            <FleetStatusWidget data={data.vehicles} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Driver Availability - clickable */}
        {isWidgetEnabled('drivers_availability') && (
          <div data-tour="dashboard-drivers">
            <DriverAvailabilityWidget data={data.driverAvailability} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Sites Status - clickable */}
        {isWidgetEnabled('sites_overview') && (
          <div data-tour="dashboard-sites">
            <SitesWidget data={data.sites} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Teams Overview - clickable */}
        {isWidgetEnabled('teams_overview') && (
          <div data-tour="dashboard-teams">
            <TeamsWidget data={data.teams} onNavigate={handleNavigate} />
          </div>
        )}

        {/* Deadlines - clickable */}
        {isWidgetEnabled('upcoming_deadlines') && (
          <DeadlinesWidget deadlines={data.deadlines} onNavigate={handleNavigate} />
        )}

        {/* Costs - clickable */}
        {isWidgetEnabled('costs_month') && (
          <CostsWidget data={data.costs} onNavigate={handleNavigate} />
        )}

        {/* Claims - clickable */}
        {isWidgetEnabled('claims_open') && (
          <ClaimsWidget data={data.claims} onNavigate={handleNavigate} />
        )}

        {/* Resource Alerts (Fluids & Wear) */}
        {isWidgetEnabled('resource_alerts') && (
          <div className={profile.id === 'directeur_flotte' ? 'col-span-2' : ''} data-tour="dashboard-resources">
            <ResourceAlertsWidget onNavigate={handleNavigate} />
          </div>
        )}
      </div>
    </div>
  );

  // Tab 2: Suivi - Monitoring & Activity
  const renderSuiviTab = () => (
    <div className="space-y-4">
      {/* Calendar - upcoming events du PARC : pas l'affaire d'un conducteur */}
      {profile.id !== 'conducteur' && <CalendarWidget onNavigate={handleNavigate} />}

      {/* Recent Activity */}
      {isWidgetEnabled('recent_activity') && (
        <ActivityWidget activities={data.activities} onNavigate={handleNavigate} />
      )}

      {/* Fleet completeness card - configuration du parc, pas l'espace conducteur */}
      {profile.id !== 'conducteur' && <CompletenessCard
        onNavigate={(section) => {
          // Map completeness sections to views
          const sectionViewMap = {
            company: 'settings',           // Company settings in admin
            vehicles: 'vehicles',          // Vehicle list
            drivers: 'drivers',            // Driver list
            assignments: 'assignments',    // Vehicle-driver assignments
            insurance: 'insurance',        // Insurance contracts
            licenses: 'drivers',           // Driver licenses in driver view
            sites: 'sites',                // Sites operational view
            maintenance: 'maintenance',    // Maintenance requests
            inspections: 'inspections',    // Vehicle inspections
            documents: 'vehicles',         // Documents are in vehicle detail view
          };
          if (sectionViewMap[section]) {
            handleNavigate(sectionViewMap[section]);
          }
        }}
      />}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Browser-style tabs */}
      <div className="flex items-center justify-between bg-white border-b border-gray-200">
        <DashboardTabs activeTab={currentTab} onTabChange={setActiveTab} tabs={tabs} />

        {/* Refresh button — `ml-auto` le garde à droite quand la barre
            d'onglets ne se dessine pas (profil conducteur). */}
        <div className="ml-auto px-4 py-2">
          <button
            onClick={refresh}
            className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 p-4 bg-white rounded-b-lg mx-4 mb-4 shadow-sm">
        {currentTab === 'actions' && renderActionsTab()}
        {currentTab === 'suivi' && renderSuiviTab()}
      </div>
    </div>
  );
}

export default Dashboard;
